import { cn } from "~/lib/utils";
import { useState } from "react";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";
import { Icon } from "../Icon";
import { isValidIconType } from "../Icon";
import { ImageLoader } from "../ImageLoader";
import { TabSelectItem } from "./types";
import { TabSelectMobile } from "./component";
import { useUser } from "~/modules/user/hooks/useUser";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RWA_COIN_ICON } from "~/consts";

// // // //

/**
 * Renders a vertical rail of tabs alongside the active tab's content.
 * Same API as TabSelectControlled — on mobile it falls back to the same
 * select dropdown; on desktop the rail scrolls (with a scroll-fade hint)
 * when the tabs are taller than the content beside them.
 */
export function VerticalTabSelectControlled<T>(props: {
    tabs: TabSelectItem<T>[];
    activeTab: TabSelectItem<T>;
    onSelectTab: (selected: TabSelectItem<T>) => void;
    children: (tab: TabSelectItem<T>) => React.ReactNode;
    name: string;
    className?: string;
    isLoggedIn: boolean;
    onTabChange?: (params: {
        prev: TabSelectItem<T>;
        next: TabSelectItem<T>;
    }) => void;
}) {
    const {
        tabs,
        activeTab,
        onSelectTab,
        name,
        className = null,
        onTabChange = null,
        isLoggedIn,
    } = props;

    // Do not render the component if there are less than two modes to switch between.
    if (tabs.length < 2) {
        return <>{props.children(activeTab)}</>;
    }

    // Function to invoke setActiveTab and onTabChange (if available)
    function setActiveTab(updatedTab: TabSelectItem<T>) {
        const prev = activeTab;
        const next = updatedTab;

        // Invoke  props.onTabChange
        if (onTabChange !== null) {
            onTabChange({ prev, next });
        }

        // Set the updated tab state
        onSelectTab(updatedTab);
    }

    return (
        <ClassnamePassthruWrapper className={className}>
            <TabSelectMobile
                activeTab={activeTab}
                tabs={tabs}
                setActiveTab={setActiveTab}
                isLoggedIn={isLoggedIn}
                name={name}
                className="mx-4 mb-3"
            />
            <div className="lg:grid lg:grid-cols-[180px_minmax(0,1fr)]">
                {/* The rail cell is position:relative with an absolutely-filled
                    scroll container, so the row height is set by the content
                    beside it and the rail scrolls when its tabs exceed it */}
                <div className="hidden lg:relative lg:block border-r">
                    <div className="scroll-fade hide-scrollbar absolute inset-0 flex flex-col overflow-y-auto overscroll-contain">
                        {tabs.map((group) => (
                            <button
                                key={group.key}
                                data-testid={group.testID}
                                onClick={() => {
                                    if (group.key === activeTab.key) return;
                                    setActiveTab(group);
                                }}
                                className={cn(
                                    "w-full whitespace-nowrap px-3 py-2 text-sm font-medium flex items-center gap-1.5 text-left transition-all",
                                    {
                                        "text-primary bg-blue-50":
                                            group.key === activeTab.key,
                                        "text-muted-foreground hover:text-secondary-foreground hover:bg-blue-50":
                                            group.key !== activeTab.key,
                                    },
                                )}
                            >
                                {group.content.icon &&
                                    isValidIconType(group.content.icon) && (
                                        <Icon
                                            icon={group.content.icon}
                                            className="h-3"
                                        />
                                    )}

                                {group.content.icon &&
                                    !isValidIconType(group.content.icon) && (
                                        <ImageLoader
                                            src={group.content.icon}
                                            fallbackSrc={RWA_COIN_ICON}
                                            className="h-4 rounded-full"
                                            alt={`${group.content.label} Icon`}
                                        />
                                    )}

                                {/* Login required indicator */}
                                {group.loginRequired && !isLoggedIn && (
                                    <FontAwesomeIcon
                                        icon={faLock}
                                        className="h-2.5 w-2.5"
                                    />
                                )}

                                <span className="truncate">
                                    {group.content.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="min-w-0">{props.children(activeTab)}</div>
            </div>
        </ClassnamePassthruWrapper>
    );
}

/**
 * Renders a vertical rail of tabs alongside the active tab's content.
 * Same API as TabSelect, but lays the tabs out vertically instead of
 * horizontally — for tab sets too long to fit in a single row.
 */
export function VerticalTabSelect<T>(props: {
    tabs: TabSelectItem<T>[];
    children: (tab: TabSelectItem<T>) => React.ReactNode;
    name: string;
    className?: string;
    onTabChange?: (params: {
        prev: TabSelectItem<T>;
        next: TabSelectItem<T>;
    }) => void;
    onTabChangeLoginRequired?: (params: {
        prev: TabSelectItem<T>;
        next: TabSelectItem<T>;
    }) => void;
}) {
    const { tabs, onTabChangeLoginRequired = null } = props;
    const { isLoggedIn } = useUser();

    // State to keep track of the active tab index
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // `tabs` can shrink while this instance is mounted (e.g. index 3 with
    // only 2 tabs left). Snap the stored index into range during render so
    // React retries with a valid selection instead of reading `undefined`.
    const safeActiveTabIndex =
        tabs.length === 0
            ? 0
            : Math.max(0, Math.min(activeTabIndex, tabs.length - 1));

    if (safeActiveTabIndex !== activeTabIndex) {
        setActiveTabIndex(safeActiveTabIndex);
    }

    const activeTab = tabs[safeActiveTabIndex];

    // Function to invoke setActiveTab and onTabChange (if available)
    const onSelectTab = (selectedTab: TabSelectItem<T>) => {
        // Invoke props.onTabChangeLoginRequired if
        // TabSelectItem.loginRequired is true and the user isn't logged in
        if (selectedTab.loginRequired && !isLoggedIn) {
            if (onTabChangeLoginRequired !== null) {
                onTabChangeLoginRequired({
                    prev: activeTab,
                    next: selectedTab,
                });
            }
            // Stop execution here
            return;
        }

        const newIndex = tabs.findIndex((tab) => tab.key === selectedTab.key);
        if (newIndex !== -1) {
            setActiveTabIndex(newIndex);
        }
    };

    return (
        <VerticalTabSelectControlled
            activeTab={activeTab}
            onSelectTab={onSelectTab}
            isLoggedIn={isLoggedIn}
            {...props}
        />
    );
}
